import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { blogListT } from "../translations/ui";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

const blogPosts = [
  {
    slug: "why-football-coaches-need-different-boots-than-players",
    title: {
      en: "Why Football Coaches Need Different Boots Than Players",
      da: "Hvorfor fodboldtrænere har brug for andre støvler end spillere",
      sv: "Varför fotbollstränare behöver andra skor än spelare",
      no: "Hvorfor fotballtrenere trenger andre støvler enn spillere",
      de: "Warum Fußballtrainer andere Schuhe brauchen als Spieler",
      fr: "Pourquoi les entraîneurs de football ont besoin de chaussures différentes",
      es: "Por qué los entrenadores de fútbol necesitan botas diferentes a las de los jugadores",
      it: "Perché gli allenatori di calcio hanno bisogno di scarpe diverse dai giocatori",
    },
    excerpt: {
      en: "The definitive guide to understanding why sideline footwear matters — and why The Gaffer was designed specifically for coaches, not the pitch.",
      da: "Den ultimative guide til at forstå, hvorfor fodtøj til sidelinjen er vigtigt — og hvorfor The Gaffer er designet specifikt til trænere, ikke banen.",
      sv: "Den definitiva guiden till att förstå varför sidlinjeskor är viktiga — och varför The Gaffer designades specifikt för tränare, inte planen.",
      no: "Den definitive guiden til å forstå hvorfor fottøy for sidelinjen er viktig — og hvorfor The Gaffer ble designet spesifikt for trenere, ikke banen.",
      de: "Der ultimative Leitfaden zum Verständnis, warum Seitenlinien-Schuhe wichtig sind — und warum The Gaffer speziell für Trainer entwickelt wurde.",
      fr: "Le guide définitif pour comprendre pourquoi les chaussures de bord de terrain comptent — et pourquoi The Gaffer a été conçu pour les entraîneurs.",
      es: "La guía definitiva para entender por qué el calzado de banda importa — y por qué The Gaffer fue diseñado específicamente para entrenadores.",
      it: "La guida definitiva per capire perché le calzature da bordo campo contano — e perché The Gaffer è stato progettato specificamente per gli allenatori.",
    },
    date: "March 24, 2026",
    readTime: "12",
  },
  {
    slug: "why-football-coaches-need-their-own-boots",
    title: {
      en: "Why Football Coaches Need Their Own Boots",
      da: "Hvorfor fodboldtrænere har brug for deres egne støvler",
      sv: "Varför fotbollstränare behöver egna skor",
      no: "Hvorfor fotballtrenere trenger sine egne støvler",
      de: "Warum Fußballtrainer ihre eigenen Schuhe brauchen",
      fr: "Pourquoi les entraîneurs de football ont besoin de leurs propres chaussures",
      es: "Por qué los entrenadores de fútbol necesitan sus propias botas",
      it: "Perché gli allenatori di calcio hanno bisogno delle proprie scarpe",
    },
    excerpt: {
      en: "Every piece of kit on a football pitch is designed for the player. The boots, the socks, the shin pads — all of it built around the demands of someone covering 10 kilometres a game. The coach covers about 200 metres. Slowly.",
      da: "Alt udstyr på en fodboldbane er designet til spilleren. Støvlerne, sokkerne, benskinnnerne — alt bygget til en der løber 10 km per kamp. Træneren går 200 meter. Langsomt.",
      sv: "All utrustning på en fotbollsplan är designad för spelaren. Skorna, strumporna, benskydden — allt byggt för någon som springer 10 km per match. Tränaren går 200 meter. Långsamt.",
      no: "Alt utstyr på en fotballbane er designet for spilleren. Støvlene, sokkene, leggskinnene — alt bygget for noen som løper 10 km per kamp. Treneren går 200 meter. Sakte.",
      de: "Jede Ausrüstung auf einem Fußballplatz ist für den Spieler konzipiert. Die Schuhe, die Socken, die Schienbeinschoner — alles für jemanden, der 10 km pro Spiel läuft. Der Trainer geht 200 Meter. Langsam.",
      fr: "Chaque équipement sur un terrain de football est conçu pour le joueur. Les chaussures, les chaussettes, les protège-tibias. L'entraîneur parcourt 200 mètres. Lentement.",
      es: "Cada pieza de equipamiento en un campo de fútbol está diseñada para el jugador. Las botas, los calcetines, las espinilleras. El entrenador recorre 200 metros. Lentamente.",
      it: "Ogni pezzo di equipaggiamento su un campo da calcio è progettato per il giocatore. Gli scarpini, i calzini, i parastinchi. L'allenatore percorre 200 metri. Lentamente.",
    },
    date: "February 15, 2026",
    readTime: "5",
  },
  {
    slug: "the-problem-with-wearing-player-boots-as-a-coach",
    title: {
      en: "The Problem With Wearing Player Boots as a Coach",
      da: "Problemet med at bruge spillerstøvler som træner",
      sv: "Problemet med att använda spelarskor som tränare",
      no: "Problemet med å bruke spillerstøvler som trener",
      de: "Das Problem mit Spielerschuhen als Trainer",
      fr: "Le problème de porter des chaussures de joueur en tant qu'entraîneur",
      es: "El problema de usar botas de jugador como entrenador",
      it: "Il problema di indossare scarpe da giocatore come allenatore",
    },
    excerpt: {
      en: "Picture this. It's a Tuesday night in November. Training session, 7pm. The pitch is wet, the temperature is hovering around four degrees, and you've been on the touchline for 75 minutes. Your feet went cold around the 30-minute mark.",
      da: "Forestil dig dette. Det er en tirsdag aften i november. Træning kl. 19. Banen er våd, temperaturen ligger omkring fire grader, og du har stået på sidelinjen i 75 minutter.",
      sv: "Föreställ dig detta. Det är en tisdag kväll i november. Träning kl. 19. Planen är blöt, temperaturen ligger runt fyra grader, och du har stått vid sidlinjen i 75 minuter.",
      no: "Se for deg dette. Det er en tirsdag kveld i november. Trening kl. 19. Banen er våt, temperaturen ligger rundt fire grader, og du har stått på sidelinjen i 75 minutter.",
      de: "Stellen Sie sich vor. Es ist ein Dienstagabend im November. Training um 19 Uhr. Der Platz ist nass, die Temperatur liegt bei vier Grad, und Sie stehen seit 75 Minuten an der Seitenlinie.",
      fr: "Imaginez. C'est un mardi soir de novembre. Entraînement à 19h. Le terrain est mouillé, la température avoisine les quatre degrés, et vous êtes en bord de terrain depuis 75 minutes.",
      es: "Imagina esto. Es un martes por la noche en noviembre. Entrenamiento a las 19h. El campo está mojado, la temperatura ronda los cuatro grados, y llevas 75 minutos en la banda.",
      it: "Immagina. È un martedì sera di novembre. Allenamento alle 19. Il campo è bagnato, la temperatura è intorno ai quattro gradi, e sei in bordo campo da 75 minuti.",
    },
    date: "February 15, 2026",
    readTime: "6",
  },
];

export default function BlogList() {
  const t = useT(blogListT);
  const lang = localStorage.getItem("ga_lang") || "en";

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
            <div className="hidden sm:block w-px h-8 bg-white/10" />
            <h1 className="hidden sm:block heading-md text-white text-lg">{t.title}</h1>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <div className="mb-12">
          <h2 className="heading-xl text-3xl md:text-4xl text-white mb-4">{t.title}</h2>
          <p className="text-white/50 body-text">{t.subtitle}</p>
        </div>

        <div className="space-y-8">
          {blogPosts.map((post) => (
            <a key={post.slug} href={`/blog/${post.slug}`} className="block group border border-white/10 hover:border-white/20 p-8 transition-all duration-300">
              <div className="flex items-center gap-4 text-white/40 text-sm mb-4">
                <span>{post.date}</span>
                <span className="w-1 h-1 bg-white/20 rounded-full" />
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {post.readTime} {t.minRead}
                </span>
              </div>
              <h3 className="heading-md text-xl md:text-2xl text-white mb-3 group-hover:text-white/80 transition-colors">
                {post.title[lang] || post.title.en}
              </h3>
              <p className="body-text text-white/50 text-sm mb-4">
                {post.excerpt[lang] || post.excerpt.en}
              </p>
              <span className="inline-flex items-center gap-2 text-white/60 group-hover:text-white text-sm uppercase tracking-wider transition-colors">
                {t.readMore}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <a href="/" className="text-white/30 hover:text-white/50 text-sm transition-colors">← {t.backHome}</a>
          <p className="text-white/30 text-sm">© 2026 Gaffer Athletics</p>
        </div>
      </footer>
    </div>
  );
}
